import { useEffect, useState } from 'react';
import { WidgetType } from 'types/widget.types';
import { calculateAspectRatioFit } from 'utils/calculateAspectRatioFit';
import { parseStrictNumber } from 'utils/number';

import { FrameShape } from './Frame/frame.types';
import { ADJUSTMENT_MAX, ADJUSTMENT_MIN, FILTER_CONFIG, FILTER_MULTIPLY_FACTOR } from './ImageWidget.config';
import { Crop, FilterEffect, FilterName, ImageWidgetData } from './ImageWidget.types';
import { VERSION } from './ImageWidget.upgrade';

interface ImageLoader {
  isLoading: boolean;
}

/**
 * A Simple hook for getting the loading state of an image url
 * e.g. const { isLoading } = useImageLoader('cat.png')
 *
 * @param url
 * @returns {ImageLoader}
 */

const loadedUrls: { [url: string]: boolean } = {};

export const useImageLoader = (url: string): ImageLoader => {
  const [isLoading, setIsLoading] = useState(!loadedUrls[url]);

  useEffect(() => {
    if (loadedUrls[url]) return;
    if (!url) setIsLoading(false);

    const img = document.createElement('img');
    img.src = url;
    img.onload = () => {
      setIsLoading(false);
      loadedUrls[url] = true;
    };

    return () => {
      if (img) img.onload = () => {};
    };
  }, [url]);

  return { isLoading };
};

interface Dimension {
  width: number;
  height: number;
}

export const generateDefaultData = (
  url: string,
  imageSize: Dimension,
  infographSize: Dimension,
  topPxOverride?: number,
  leftPxOverride?: number,
  isColorOverlayEnabled?: boolean,
  cropOverride?: Crop,
) => {
  const { width: widthPx, height: heightPx } = calculateAspectRatioFit(
    imageSize.width,
    imageSize.height,
    // The new image widget is limited to max half the size of the page
    infographSize.width / 2,
    infographSize.height / 2,
  );

  const imageWidget = {
    widgetType: WidgetType.Image,
    widgetData: {
      version: VERSION,
      topPx: topPxOverride || 0,
      leftPx: leftPxOverride || 0,
      rotateDeg: 0,
      widthPx,
      heightPx,
      url: url,
      altText: '',
      isDecorative: false,
      crop: cropOverride ?? {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      imageRect: {
        widthPx: widthPx,
        heightPx: heightPx,
      },
      frame: FrameShape.None,
      opacity: 1,
      filter: {
        appliedFilterName: 'None' as FilterName,
        filterIntensity: 0,
        colorOverlay: {
          isEnabled: Boolean(isColorOverlayEnabled),
          color: '#CCCCCC',
          opacity: 50,
        },
      },
    },
  };

  return imageWidget;
};

/**
 * Get the absolute positioning style of an element
 */
export const parseElementAbsPosition = (el: HTMLElement): [number, number, number, number] => {
  const top = Math.abs(parseStrictNumber(el.style.top));
  const right = Math.abs(parseStrictNumber(el.style.right));
  const bottom = Math.abs(parseStrictNumber(el.style.bottom));
  const left = Math.abs(parseStrictNumber(el.style.left));

  return [top, right, bottom, left];
};

/**
 * Get the absolute position of the crop area
 */
export const cropToAbsolutePosition = (crop: Crop): [number, number, number, number] => {
  const top = -crop.top;
  const right = crop.right;
  const bottom = crop.bottom;
  const left = -crop.left;

  return [top, right, bottom, left];
};

/**
 * Recalculate filter adjustment.
 *
 * @param key Brightness or contrast or saturation
 * @param intensity Filter intensity
 * @param appliedFilterName Name of currently applied filter
 * @returns {number}
 */
export const calculateFilterAdjustment = (
  key: keyof FilterEffect,
  intensity: number,
  appliedFilterName: FilterName,
) => {
  const percentage = intensity / 100;
  const result = Number(FILTER_CONFIG[appliedFilterName][key]!) * percentage;

  if (result < ADJUSTMENT_MIN) {
    return -100;
  }

  if (result > ADJUSTMENT_MAX) {
    return 100;
  }

  return result < 0 ? Math.floor(result) : Math.ceil(result);
};

/**
 * Convert FilterEffect object into css filter value in string.
 *
 * @param filters {FilterEffect}
 * @param filterIntensity Filter intensity value.
 * @param id Unique id
 * @returns {string}
 */
export const toStyleFiltersString = (filters: FilterEffect, filterIntensity: number, id: string) => {
  const percentage = filterIntensity / 100;
  let filtersString = Object.entries(filters).reduce((acc, [k, v]) => {
    switch (k) {
      case 'saturation':
        return `${acc} saturate(${100 + v * percentage}%)`;
      case 'brightness':
      case 'contrast':
        return `${acc} ${k}(${100 + v * percentage * FILTER_MULTIPLY_FACTOR}%)`;
      case 'tint':
      case 'filterUrl':
      default:
        return acc;
    }
  }, '');

  if (Boolean(filters.tint)) {
    filtersString = `${filtersString} url(#vg_filter_id__canvas__${id})`;
  }

  return filtersString;
};

/**
 * Check if the image widget has a overlay enabled.
 * @param imageWidgetData {ImageWidgetData}
 * @returns {boolean}
 */
export const hasOverlay = (imageWidgetData: ImageWidgetData) => !!imageWidgetData.filter?.colorOverlay?.isEnabled;

/**
 * Get the overlay opacity of the image widget.
 * @param imageWidgetData {ImageWidgetData}
 * @returns {number}
 */
export const getOverlayTransparency = (imageWidgetData: ImageWidgetData) => {
  if (!hasOverlay(imageWidgetData)) return 0;
  return imageWidgetData.filter?.colorOverlay?.opacity || 0;
};
